/**
 * @license
 * Copyright 2025 Steven Roussey <sroussey@gmail.com>
 * SPDX-License-Identifier: Apache-2.0
 */

import { createServiceToken, EventEmitter, makeFingerprint } from "@workglow/util";
import { DataPortSchemaObject, FromSchema, TypedArraySchemaOptions } from "@workglow/util/schema";
import {
  ITabularMigration,
  ITabularMigrationApplier,
  runTabularMigrations,
  RunTabularMigrationsOptions,
} from "../migrations";
import { CoveringIndexMissingError } from "./CoveringIndexMissingError";
import {
  assertCursorMatches,
  CursorPayload,
  decodeCursor,
  encodeCursor,
  PageCursor,
} from "./Cursor";
import {
  AnyTabularStorage,
  AutoGeneratedKeys,
  CoveringIndexQueryOptions,
  DeleteSearchCriteria,
  InsertEntity,
  isSearchCondition,
  ITabularStorage,
  OrderBy,
  Page,
  PageRequest,
  QueryOptions,
  SearchCondition,
  SearchCriteria,
  SimplifyPrimaryKey,
  TabularChangePayload,
  TabularEventListener,
  TabularEventListeners,
  TabularEventName,
  TabularEventParameters,
  TabularSubscribeOptions,
  ValueOptionType,
} from "./ITabularStorage";
import {
  StorageEmptyCriteriaError,
  StorageInvalidColumnError,
  StorageInvalidLimitError,
  StorageUnsupportedError,
  StorageValidationError,
} from "./StorageError";

export const TABULAR_REPOSITORY = createServiceToken<AnyTabularStorage>(
  "storage.tabularRepository"
);

/**
 * Converts a row column value into a JSON-serialisable form for cursor
 * encoding, and is also used by the in-memory comparator so that row
 * values and decoded cursor values are normalised the same way before
 * comparison. Returning `string | number | boolean | null` keeps the
 * comparison space simple (lexicographic on strings, numeric on numbers).
 *
 * - `Date` becomes its ISO 8601 string. ISO 8601 sorts chronologically
 *   under lexicographic comparison, which is the property we rely on to
 *   compare a row's `Date` against a cursor's encoded date string.
 * - `bigint` is rejected. JS `bigint > bigint` works numerically, but
 *   once a bigint is encoded into the cursor as a decimal string, naive
 *   string comparison no longer matches the numeric order (`"10" < "9"`).
 *   Mixing the two in the in-memory comparator would silently mis-order
 *   pages. Until the cursor format carries explicit type information,
 *   keyset paging on a bigint column is unsupported — fail loudly so
 *   callers either change the column type or override `getPage`.
 * - `Uint8Array` and other object types are not orderable as cursor keys.
 */
function toCursorValue(value: unknown): string | number | boolean | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }
  if (value instanceof Date) return value.toISOString();
  // Use `StorageValidationError` (not generic `Error`) so the failure
  // mode for an unsupported key type matches the rest of the cursor
  // codec — `decodeCursor` already throws the same class for malformed
  // input, and downstream callers translating cursor failures into 4xx
  // responses can catch one consistent type instead of an unclassified
  // 500.
  if (typeof value === "bigint") {
    throw new StorageValidationError(
      "bigint values are not supported as cursor keys — string-encoded bigints don't sort numerically. Use a number column or override `getPage` for this storage."
    );
  }
  throw new StorageValidationError(
    `Cannot encode value of type ${typeof value} into a pagination cursor; use a key column with a primitive type.`
  );
}

/**
 * Compares two cursor key values, normalising each side through
 * {@link toCursorValue} first so a row's runtime value (which may be a
 * `Date` or other rich type) is comparable against a cursor's decoded
 * primitive. Nulls sort before non-nulls, then standard `<`/`>`. Booleans
 * are coerced to numbers (`false` < `true`) so the sort and the keyset
 * filter can never disagree on boolean ordering. Returns -1, 0, or 1.
 */
function compareKeyValues(a: unknown, b: unknown): number {
  const an = toCursorValue(a);
  const bn = toCursorValue(b);
  if (an === null && bn === null) return 0;
  if (an === null) return -1;
  if (bn === null) return 1;
  const av = typeof an === "boolean" ? Number(an) : an;
  const bv = typeof bn === "boolean" ? Number(bn) : bn;
  if (av < bv) return -1;
  if (av > bv) return 1;
  return 0;
}

/** Controls how client-provided values for auto-generated keys are handled. */
export type ClientProvidedKeysOption = "never" | "if-missing" | "always";

export type KeyGenerationStrategy = "autoincrement" | "uuid";

/**
 * Abstract base class for tabular storage repositories. Splits a schema into
 * primary-key and value halves, manages indexes and `x-auto-generated` PK
 * columns, and emits {@link TabularEventListeners} events for all reads and
 * mutations.
 */
export abstract class BaseTabularStorage<
  Schema extends DataPortSchemaObject,
  PrimaryKeyNames extends ReadonlyArray<keyof Schema["properties"]>,
  // computed types
  Entity = FromSchema<Schema, TypedArraySchemaOptions>,
  PrimaryKey = SimplifyPrimaryKey<Entity, PrimaryKeyNames>,
  Value = Omit<Entity, PrimaryKeyNames[number] & keyof Entity>,
  InsertType = InsertEntity<Entity, AutoGeneratedKeys<Schema>>,
> implements ITabularStorage<Schema, PrimaryKeyNames, Entity, PrimaryKey, InsertType> {
  protected events = new EventEmitter<TabularEventListeners<PrimaryKey, Entity>>();

  protected indexes: Array<keyof Entity>[];
  /**
   * Compound column tuples that must each be unique across all rows in the
   * table. Concrete backends translate these into DB-level UNIQUE indexes
   * (SQLite / Postgres `CREATE UNIQUE INDEX IF NOT EXISTS`) so the
   * deduplication invariant survives outside the application-layer code that
   * upserts canonical rows. Stored separately from {@link indexes} so the
   * prefix-redundancy filter applied to plain indexes does not collapse a
   * unique tuple that happens to share a prefix.
   */
  protected uniqueIndexes: Array<keyof Entity>[];
  protected primaryKeySchema: DataPortSchemaObject;
  protected valueSchema: DataPortSchemaObject;

  /**
   * Optional declarative migrations evolving older deployments to the
   * current target schema. When set + non-empty, `setupDatabase()` on
   * concrete subclasses delegates to {@link applyTabularMigrations}.
   */
  protected readonly tabularMigrations: ReadonlyArray<ITabularMigration> | undefined;

  /**
   * Default component name used when an `ITabularMigration.component` is
   * omitted. Defaults to `tabular:${storageName}` once the subclass has
   * supplied a name.
   */
  protected migrationComponent: string = "tabular:unnamed";

  /** At most one primary-key column may be marked `x-auto-generated: true`. */
  protected autoGeneratedKeyName: keyof Entity | null = null;
  protected autoGeneratedKeyStrategy: KeyGenerationStrategy | null = null;
  protected clientProvidedKeys: ClientProvidedKeysOption;

  constructor(
    protected schema: Schema,
    protected primaryKeyNames: PrimaryKeyNames,
    indexes: readonly (keyof NoInfer<Entity> | readonly (keyof NoInfer<Entity>)[])[] = [],
    clientProvidedKeys: ClientProvidedKeysOption = "if-missing",
    tabularMigrations?: ReadonlyArray<ITabularMigration>,
    migrationName?: string,
    uniqueIndexes: readonly (readonly (keyof NoInfer<Entity>)[])[] = []
  ) {
    this.tabularMigrations = tabularMigrations;
    if (migrationName) {
      this.migrationComponent = `tabular:${migrationName}`;
    }
    this.clientProvidedKeys = clientProvidedKeys;
    const primaryKeyProps: Record<string, any> = {};
    const valueProps: Record<string, any> = {};
    const primaryKeySet = new Set(primaryKeyNames);

    for (const [key, typeDef] of Object.entries(schema.properties)) {
      if (primaryKeySet.has(key as keyof Schema["properties"])) {
        primaryKeyProps[key] = Object.assign({}, typeDef);
      } else {
        valueProps[key] = Object.assign({}, typeDef);
      }
    }

    const primaryKeyRequired =
      schema.required?.filter((key) => primaryKeySet.has(key as keyof Schema["properties"])) ?? [];
    const valueRequired =
      schema.required?.filter((key) => !primaryKeySet.has(key as keyof Schema["properties"])) ?? [];

    this.primaryKeySchema = {
      type: "object",
      properties: primaryKeyProps,
      required: primaryKeyRequired,
      additionalProperties: false,
    } as DataPortSchemaObject;
    this.valueSchema = {
      type: "object",
      properties: valueProps,
      required: valueRequired,
      additionalProperties: false,
    } as DataPortSchemaObject;

    const combinedColumns = [...this.primaryKeyColumns(), ...this.valueColumns()];
    for (const column of combinedColumns) {
      if (typeof column !== "string") {
        throw new Error("Column names must be strings");
      }
      if (!/^[a-z]\w*$/i.test(column)) {
        throw new Error(
          "Column names must start with a letter and contain only letters, digits, and underscores"
        );
      }
    }

    this.indexes = indexes.map((spec) => (Array.isArray(spec) ? spec : [spec])) as Array<
      Array<keyof Entity>
    >;

    // Drop any compound index that is a prefix of another compound index or
    // of the primary key — it's redundant for lookups.
    this.indexes = this.filterCompoundKeys(
      this.primaryKeyColumns() as unknown as Array<keyof Entity>,
      this.indexes
    );

    for (const compoundIndex of this.indexes) {
      for (const column of compoundIndex) {
        if (
          !(column in this.primaryKeySchema.properties) &&
          !(column in this.valueSchema.properties)
        ) {
          throw new Error(
            `Searchable column ${String(column)} is not in the primary key schema or value schema`
          );
        }
      }
    }

    // Validate unique index column names against the schema the same way
    // `indexes` are validated. NULL semantics and DDL emission live in the
    // concrete backends.
    this.uniqueIndexes = uniqueIndexes.map((spec) => [...spec]) as Array<Array<keyof Entity>>;
    for (const uniqueIndex of this.uniqueIndexes) {
      if (uniqueIndex.length === 0) {
        throw new Error("Unique index column list must not be empty");
      }
      for (const column of uniqueIndex) {
        if (
          !(column in this.primaryKeySchema.properties) &&
          !(column in this.valueSchema.properties)
        ) {
          throw new Error(
            `Unique-index column ${String(column)} is not in the primary key schema or value schema`
          );
        }
      }
    }

    // Drop any regular index whose column tuple exactly matches a declared
    // unique index — a UNIQUE index is also a B-tree on the same columns in
    // the same order, so the non-unique copy is pure waste (duplicate disk +
    // duplicate write on every row). Prefix relationships are intentionally
    // NOT collapsed here: the existing single-column carve-out in
    // `filterCompoundKeys` reflects that a dedicated narrow index can still be
    // a deliberate optimization vs a wider unique tuple's leftmost-prefix
    // scan.
    const uniqueTupleKeys = new Set(this.uniqueIndexes.map((tuple) => tuple.map(String).join(" ")));
    this.indexes = this.indexes.filter((tuple) => !uniqueTupleKeys.has(tuple.map(String).join(" ")));

    // Detect and validate auto-generated keys (at most one PK column; any PK position is allowed).
    // Composite keys often put a scope column first (e.g. kb_id) and auto-generate a second id.
    const autoGeneratedKeys: string[] = [];
    for (const key of primaryKeyNames) {
      const keyStr = String(key);
      const propDef = (schema.properties as any)[keyStr];
      if (propDef && typeof propDef === "object" && "x-auto-generated" in propDef) {
        if (propDef["x-auto-generated"] === true) {
          autoGeneratedKeys.push(keyStr);
        }
      }
    }

    if (autoGeneratedKeys.length > 1) {
      throw new Error(
        `Multiple auto-generated keys detected: ${autoGeneratedKeys.join(", ")}. ` +
          `At most one primary key column can be auto-generated.`
      );
    }

    if (autoGeneratedKeys.length > 0) {
      const autoGenKeyName = autoGeneratedKeys[0];

      this.autoGeneratedKeyName = autoGenKeyName as keyof Entity;
      this.autoGeneratedKeyStrategy = this.determineGenerationStrategy(
        autoGenKeyName,
        schema.properties[autoGenKeyName as keyof Schema["properties"]]
      );
    }
  }

  protected filterCompoundKeys(
    primaryKey: Array<keyof Entity>,
    potentialKeys: Array<keyof Entity>[]
  ): Array<keyof Entity>[] {
    const isPrefix = (prefix: Array<keyof Entity>, arr: Array<keyof Entity>): boolean => {
      if (prefix.length > arr.length) return false;
      return prefix.every((val, index) => val === arr[index]);
    };

    potentialKeys.sort((a, b) => a.length - b.length);

    let filteredKeys: Array<keyof Entity>[] = [];

    for (let i = 0; i < potentialKeys.length; i++) {
      let key = potentialKeys[i];

      if (isPrefix(key, primaryKey)) continue;

      // Always keep single-column indexes even if they'd be prefix-redundant.
      if (key.length === 1) {
        filteredKeys.push(key);
        continue;
      }

      let isRedundant = potentialKeys.some((otherKey, j) => j > i && isPrefix(key, otherKey));

      if (!isRedundant) {
        filteredKeys.push(key);
      }
    }

    return filteredKeys;
  }

  on<Event extends TabularEventName>(
    name: Event,
    fn: TabularEventListener<Event, PrimaryKey, Entity>
  ) {
    this.events.on(name, fn);
  }

  off<Event extends TabularEventName>(
    name: Event,
    fn: TabularEventListener<Event, PrimaryKey, Entity>
  ) {
    this.events.off(name, fn);
  }

  once<Event extends TabularEventName>(
    name: Event,
    fn: TabularEventListener<Event, PrimaryKey, Entity>
  ) {
    this.events.once(name, fn);
  }

  emit<Event extends TabularEventName>(
    name: Event,
    ...args: TabularEventParameters<Event, PrimaryKey, Entity>
  ) {
    this.events.emit(name, ...args);
  }

  waitOn<Event extends TabularEventName>(
    name: Event
  ): Promise<TabularEventParameters<Event, PrimaryKey, Entity>> {
    return this.events.waitOn(name) as Promise<TabularEventParameters<Event, PrimaryKey, Entity>>;
  }

  abstract put(value: InsertType): Promise<Entity>;
  abstract putBulk(values: InsertType[]): Promise<Entity[]>;
  abstract get(key: PrimaryKey): Promise<Entity | undefined>;
  abstract delete(key: PrimaryKey | Entity): Promise<void>;
  abstract getAll(options?: QueryOptions<Entity>): Promise<Entity[] | undefined>;
  abstract deleteAll(): Promise<void>;
  abstract size(): Promise<number>;

  /**
   * Default plural-get implementation: parallel single-key fetches via
   * {@link get}, with `undefined` results filtered out. Backends with a
   * cheaper batched path (SQL `WHERE pk IN (...)`) override this.
   */
  async getBulk(keys: readonly PrimaryKey[]): Promise<Entity[]> {
    if (keys.length === 0) return [];
    const results = await Promise.all(keys.map((k) => this.get(k)));
    const found = results.filter((r) => r !== undefined) as Entity[];
    this.events.emit("getBulk", keys, found);
    return found;
  }

  /** Concrete storage implementations can override this with native count APIs. */
  async count(criteria?: SearchCriteria<Entity>): Promise<number> {
    if (!criteria || Object.keys(criteria).length === 0) {
      return await this.size();
    }
    const entities = await this.query(criteria);
    return entities?.length ?? 0;
  }

  abstract deleteSearch(criteria: DeleteSearchCriteria<Entity>): Promise<void>;

  abstract query(
    criteria: SearchCriteria<Entity>,
    options?: QueryOptions<Entity>
  ): Promise<Entity[] | undefined>;

  /**
   * Strict, projected query served entirely by a covering compound index.
   * Subclasses that support index-only reads (e.g. IndexedDB, InMemory) should
   * override this with an efficient implementation. The default throws
   * {@link CoveringIndexMissingError} so callers get a consistent error when
   * the storage backend has not implemented index-only projection.
   */
  queryIndex<K extends keyof Entity & string>(
    criteria: SearchCriteria<Entity>,
    options: CoveringIndexQueryOptions<Entity, K>
  ): Promise<Pick<Entity, K>[]> {
    this.validateSelect(options);
    const requiredColumns = [
      ...Object.keys(criteria),
      ...(options.orderBy ?? []).map((o) => String(o.column)),
      ...options.select.map(String),
    ];
    throw new CoveringIndexMissingError(this.constructor.name, requiredColumns, []);
  }

  abstract getOffsetPage(offset: number, limit: number): Promise<Entity[] | undefined>;

  /** Cursor-based iteration, stable under concurrent writes. */
  async *records(pageSize: number = 100): AsyncGenerator<Entity, void, undefined> {
    if (pageSize <= 0) {
      throw new RangeError(`pageSize must be greater than 0, got ${pageSize}`);
    }
    let cursor: PageCursor | undefined;
    while (true) {
      const page = await this.getPage({ limit: pageSize, cursor });
      for (const entity of page.items) {
        yield entity;
      }
      if (!page.nextCursor || page.items.length === 0) break;
      cursor = page.nextCursor;
    }
  }

  /**
   * Cursor-based iteration, stable under concurrent writes. Each yielded
   * array is a fresh copy of `Page.items` so callers can mutate it safely.
   */
  async *pages(pageSize: number = 100): AsyncGenerator<Entity[], void, undefined> {
    if (pageSize <= 0) {
      throw new RangeError(`pageSize must be greater than 0, got ${pageSize}`);
    }
    let cursor: PageCursor | undefined;
    while (true) {
      const page = await this.getPage({ limit: pageSize, cursor });
      if (page.items.length > 0) {
        yield page.items.slice() as Entity[];
      }
      if (!page.nextCursor || page.items.length === 0) break;
      cursor = page.nextCursor;
    }
  }

  /**
   * Default keyset-pagination implementation. Backends that can express
   * tuple comparison natively (SQL row-value comparisons) should override
   * this for efficiency, but the default works against any implementation
   * of {@link query} / {@link getAll}.
   */
  async getPage(request: PageRequest<Entity> = {}): Promise<Page<Entity>> {
    return this.runPage(undefined, request);
  }

  /** Default cursor-paginated form of {@link query}. */
  async queryPage(
    criteria: SearchCriteria<Entity>,
    request: PageRequest<Entity> = {}
  ): Promise<Page<Entity>> {
    return this.runPage(criteria, request);
  }

  /**
   * Shared keyset-pagination engine for both {@link getPage} and
   * {@link queryPage}. Translates the cursor into AND criteria using the
   * primary key column(s) and runs the existing {@link query}/{@link getAll}
   * machinery, then re-encodes the position of the last returned row.
   *
   * For composite primary keys this performs the keyset comparison in
   * memory after fetching a candidate window. SQL backends should override
   * {@link getPage}/{@link queryPage} to push it down to the database.
   */
  protected async runPage(
    criteria: SearchCriteria<Entity> | undefined,
    request: PageRequest<Entity>
  ): Promise<Page<Entity>> {
    this.validatePageRequest(request);
    const limit = request.limit ?? 100;

    const pkColumns = this.primaryKeyColumns() as unknown as Array<keyof Entity>;
    const orderBy = request.orderBy;
    const effectiveOrderBy = this.buildEffectiveOrderBy(orderBy, pkColumns);
    const effectiveOrderForCursor = effectiveOrderBy.map((o) => ({
      column: String(o.column),
      direction: o.direction,
    }));

    let cursorPayload: CursorPayload | undefined;
    if (request.cursor !== undefined) {
      cursorPayload = decodeCursor(request.cursor);
      assertCursorMatches(cursorPayload, effectiveOrderForCursor);
    }

    const pkCol = pkColumns[0];
    const userCriteria = criteria ?? ({} as SearchCriteria<Entity>);
    const userTouchesPk =
      pkColumns.length === 1 && Object.prototype.hasOwnProperty.call(userCriteria, pkCol);

    // The simple-keyset path pushes one `pk OP ?` predicate into `query()`.
    // It works whenever the effective ordering is exactly "primary key in
    // some direction" — that's true both when the caller passed no orderBy
    // (we default to PK ASC) and when they passed exactly the primary key
    // themselves (any direction). We still bail out if the caller already
    // has a criterion on the PK column, because we'd otherwise overwrite it.
    const canPushKeyset =
      pkColumns.length === 1 &&
      effectiveOrderBy.length === 1 &&
      effectiveOrderBy[0].column === pkCol &&
      !userTouchesPk;

    let queryCriteria: SearchCriteria<Entity> = userCriteria;
    // Three pushdown tiers, in decreasing precision:
    //
    //   1. Simple keyset (`canPushKeyset` true): the effective ordering is
    //      a single PK column, so one `pk OP ?` inequality is exactly the
    //      keyset filter. Backend returns at most `limit` correct rows.
    //
    //   2. Leading-column pushdown (the fallback below): the effective
    //      ordering is compound (or non-PK + PK tiebreaker). We can't
    //      express the full keyset through `query()` (which is AND-only),
    //      but we *can* push the leading column's inequality to prune the
    //      bulk of the table, then sort and apply the full multi-column
    //      keyset in memory. Bounded fetch as long as the leading column
    //      isn't degenerate (single value across the whole table).
    //
    //   3. Full scan (last resort): leading column has a NULL cursor value
    //      we can't express, or the user already filters on that column,
    //      or there's no cursor (page 1). We fetch everything, sort, slice.
    //      Backends with tuple comparison should override `getPage` to
    //      avoid this.
    const useFallback = !canPushKeyset;

    if (cursorPayload && canPushKeyset) {
      const direction = effectiveOrderBy[0].direction;
      const op = direction === "ASC" ? ">" : "<";
      // canPushKeyset implies a single-column PK as the only effective
      // ordering, so cursorPayload has exactly one entry and `c[0]` is the
      // last seen PK value. PK columns are NOT NULL so there's no need for
      // the IS NULL gymnastics the SQL/in-memory keyset paths handle.
      // The simple-keyset path's correctness depends on the backend's
      // `query()` calling `jsToSqlValue` (or equivalent) on the
      // SearchCondition value — every concrete backend in this monorepo
      // does, but a third-party backend would need to as well.
      const lastPk = cursorPayload.c[0] as Entity[keyof Entity];
      const keysetCondition: SearchCondition<Entity[keyof Entity]> = {
        value: lastPk,
        operator: op,
      };
      queryCriteria = {
        ...userCriteria,
        [pkCol]: keysetCondition,
      } as SearchCriteria<Entity>;
    } else if (cursorPayload && useFallback) {
      // Tier 2: prune the bulk of the table by pushing the leading
      // ordering column's inequality into `query()`. We push only for
      // ASC because:
      //   - ASC NULLs-first: rows `>= cursor[0]` include the cursor row
      //     plus anything strictly greater; the in-memory keyset filter
      //     then drops the cursor row and any earlier-on-tiebreaker rows.
      //     NULL rows (which sort before in ASC NULLs-first) are
      //     correctly excluded by `>=` because SQL `null >= x` is null.
      //   - DESC NULLs-last would need `(col <= ? OR col IS NULL)` to
      //     keep the NULL trailer rows that sort *after* the cursor.
      //     `query()` is AND-only and can't express the OR, so pushing
      //     `col <= ?` would silently drop the NULL trailer. We fall
      //     through to a full scan instead — slower, but correct on
      //     nullable DESC orderings. SQL backends override `getPage`
      //     entirely so they don't pay this price.
      // Skipped when the user already filters on the leading column
      // (we'd trample their criterion) or the cursor's leading value
      // is NULL (no representable `col > NULL` in NULLs-first ASC).
      const leading = effectiveOrderBy[0];
      const leadingCol = leading.column;
      const leadingCursor = cursorPayload.c[0];
      const userTouchesLeading = Object.prototype.hasOwnProperty.call(userCriteria, leadingCol);
      if (leading.direction === "ASC" && leadingCursor !== null && !userTouchesLeading) {
        const leadingCondition: SearchCondition<Entity[keyof Entity]> = {
          value: leadingCursor as Entity[keyof Entity],
          operator: ">=",
        };
        queryCriteria = {
          ...userCriteria,
          [leadingCol]: leadingCondition,
        } as SearchCriteria<Entity>;
      }
    }

    const fetchLimit = useFallback ? undefined : limit;

    const queryOptions: QueryOptions<Entity> = {
      orderBy: effectiveOrderBy,
      ...(fetchLimit !== undefined ? { limit: fetchLimit } : {}),
    };

    let rows: Entity[] | undefined;
    // Tracks whether we wound up using `getAll()` because `query()` was
    // unsupported and we fell back. In that case the backend never
    // applied our keyset / leading-column predicate, so we must drive
    // the in-memory sort + keyset filter ourselves even if `useFallback`
    // is false (which it would be when the *simple-keyset* path tried to
    // push `pk OP ?` to `query()` and got rejected — without this flag
    // the cursor predicate would never run and pages would repeat rows).
    let forcedFallback = false;
    if (Object.keys(queryCriteria).length === 0) {
      rows = await this.getAll(queryOptions);
    } else {
      try {
        rows = await this.query(queryCriteria, queryOptions);
      } catch (err) {
        // Backends like FsFolder don't implement `query()`. Falling back
        // to a full scan keeps the call correct as long as we then sort
        // and filter in memory ourselves. User-supplied criteria still
        // need to fail loudly, so we only swallow the error when we'd
        // added our own pushdown criterion AND the user passed none.
        const userHadNoCriteria = !criteria || Object.keys(criteria).length === 0;
        if (err instanceof StorageUnsupportedError && userHadNoCriteria) {
          rows = await this.getAll({ orderBy: effectiveOrderBy });
          forcedFallback = true;
        } else {
          throw err;
        }
      }
    }

    let items: Entity[] = rows ?? [];

    if (useFallback || forcedFallback) {
      // Keyset paging requires rows to be seen in a definite order. Some
      // backends don't honour `orderBy` reliably (FsFolder, certain remote
      // services), so we re-sort here to guarantee correctness rather than
      // trust the backend.
      items = this.sortInMemory(items.slice(), effectiveOrderBy);
      if (cursorPayload) {
        items = this.applyKeysetFilter(items, cursorPayload, effectiveOrderBy, pkColumns);
      }
    }

    if (items.length > limit) {
      items = items.slice(0, limit);
    }

    const nextCursor =
      items.length === limit
        ? this.buildCursor(items[items.length - 1], effectiveOrderBy)
        : undefined;

    return { items, nextCursor };
  }

  /**
   * Returns the order spec actually used to drive a keyset page: the
   * caller's `orderBy` (if any) followed by every primary-key column as a
   * stable tiebreaker. PK columns are appended in ASC direction unless the
   * caller already specified a direction for them.
   */
  protected buildEffectiveOrderBy(
    orderBy: ReadonlyArray<OrderBy<Entity>> | undefined,
    pkColumns: ReadonlyArray<keyof Entity>
  ): ReadonlyArray<OrderBy<Entity>> {
    const result: OrderBy<Entity>[] = orderBy ? orderBy.slice() : [];
    const seen = new Set(result.map((o) => o.column));
    for (const pk of pkColumns) {
      if (!seen.has(pk)) {
        result.push({ column: pk, direction: "ASC" });
      }
    }
    return result;
  }

  /**
   * Sorts rows in place by the given order spec, using the same null-first
   * comparison rules as the cursor filter so results are consistent.
   */
  protected sortInMemory(rows: Entity[], orderBy: ReadonlyArray<OrderBy<Entity>>): Entity[] {
    rows.sort((a, b) => {
      for (const { column, direction } of orderBy) {
        // `compareKeyValues` runs each side through `toCursorValue`, so
        // we don't need (and shouldn't) coerce types here — passing the
        // raw row value lets `Date` (and any other type the encoder
        // supports) compare correctly against a decoded cursor value.
        const cmp = compareKeyValues(a[column], b[column]);
        if (cmp !== 0) return direction === "ASC" ? cmp : -cmp;
      }
      return 0;
    });
    return rows;
  }

  /**
   * In-memory keyset filter: drops rows that come at or before the cursor
   * row according to `orderBy + primaryKey`. Used by the default
   * implementation when the backend can't express tuple comparison.
   */
  protected applyKeysetFilter(
    rows: Entity[],
    cursor: CursorPayload,
    effectiveOrderBy: ReadonlyArray<OrderBy<Entity>>,
    _pkColumns: ReadonlyArray<keyof Entity>
  ): Entity[] {
    return rows.filter((row) => {
      for (let i = 0; i < effectiveOrderBy.length; i++) {
        const { column, direction } = effectiveOrderBy[i];
        // `compareKeyValues` normalises both sides through `toCursorValue`,
        // so passing the raw row value here lets a `Date` row column be
        // compared against the decoded ISO string in the cursor without a
        // type-mismatch surprise.
        const cmp = compareKeyValues(row[column], cursor.c[i]);
        if (cmp === 0) continue;
        return direction === "ASC" ? cmp > 0 : cmp < 0;
      }
      return false; // exact-match row was already returned in the prior page
    });
  }

  /**
   * Encodes the position of `row` as an opaque cursor. The caller passes
   * the effective ordering it just used — caller-supplied `orderBy`
   * followed by any primary-key columns not already covered by `orderBy`
   * — and this writes one value per effective column, in the same order.
   * Computing that ordering is the caller's responsibility because they
   * already have it in hand and we don't want to recompute it here.
   */
  protected buildCursor(row: Entity, effectiveOrderBy: ReadonlyArray<OrderBy<Entity>>): PageCursor {
    const n = effectiveOrderBy.map((spec) => String(spec.column));
    const d = effectiveOrderBy.map((spec): "a" | "d" => (spec.direction === "ASC" ? "a" : "d"));
    const c = effectiveOrderBy.map((spec) => toCursorValue(row[spec.column]));
    return encodeCursor({ v: 1, n, d, c });
  }

  /** Default throws — override in subclasses that support subscriptions. */
  public subscribeToChanges(
    _callback: (change: TabularChangePayload<Entity>) => void,
    _options?: TabularSubscribeOptions
  ): () => void {
    throw new Error(
      `subscribeToChanges is not implemented for ${this.constructor.name}. ` +
        `All concrete repository implementations should override this method.`
    );
  }

  /**
   * Validates query parameters: criteria columns, options, and operator values.
   * @throws StorageEmptyCriteriaError if criteria is empty
   * @throws StorageInvalidLimitError if limit <= 0
   * @throws StorageValidationError if offset < 0
   * @throws StorageInvalidColumnError if any column name is not in the schema
   */
  protected validateQueryParams(
    criteria: SearchCriteria<Entity>,
    options?: QueryOptions<Entity>
  ): void {
    const criteriaKeys = Object.keys(criteria) as Array<keyof Entity>;

    if (criteriaKeys.length === 0) {
      throw new StorageEmptyCriteriaError();
    }

    if (options?.limit !== undefined && options.limit <= 0) {
      throw new StorageInvalidLimitError(options.limit);
    }

    if (options?.offset !== undefined && options.offset < 0) {
      throw new StorageValidationError(`Query offset must be non-negative, got ${options.offset}`);
    }

    for (const column of criteriaKeys) {
      if (!(column in this.schema.properties)) {
        throw new StorageInvalidColumnError(String(column));
      }
      const criterion = criteria[column];
      if (isSearchCondition(criterion)) {
        const validOperators = ["=", "<", "<=", ">", ">="];
        if (!validOperators.includes(criterion.operator)) {
          throw new StorageValidationError(
            `Invalid operator "${criterion.operator}". Must be one of: ${validOperators.join(", ")}`
          );
        }
      }
    }

    this.validateOrderBy(options?.orderBy);
  }

  protected validateGetAllOptions(options?: QueryOptions<Entity>): void {
    if (!options) return;

    if (options.limit !== undefined && options.limit <= 0) {
      throw new StorageInvalidLimitError(options.limit);
    }

    if (options.offset !== undefined && options.offset < 0) {
      throw new StorageValidationError(`Query offset must be non-negative, got ${options.offset}`);
    }

    this.validateOrderBy(options.orderBy);
  }

  /**
   * Validates the `orderBy` clause of a {@link PageRequest}: column names
   * must exist in the schema and directions must be `"ASC"` or `"DESC"`.
   *
   * Page paths (`getPage`/`queryPage`) must call this before any backend
   * builds SQL, because column names from `request.orderBy` end up
   * interpolated directly into `ORDER BY` and the keyset `WHERE` clauses.
   * Without this guard a caller passing arbitrary strings would either
   * trigger a backend SQL error or, worse, allow SQL injection on backends
   * that emit unquoted identifiers.
   */
  protected validateOrderBy(orderBy: ReadonlyArray<OrderBy<Entity>> | undefined): void {
    if (!orderBy) return;
    const validDirections = ["ASC", "DESC"];
    for (const { column, direction } of orderBy) {
      if (typeof column !== "string") {
        throw new StorageInvalidColumnError(String(column));
      }
      if (!(column in this.schema.properties)) {
        throw new StorageInvalidColumnError(String(column));
      }
      if (!validDirections.includes(direction)) {
        throw new StorageValidationError(
          `Invalid sort direction "${direction}". Must be "ASC" or "DESC"`
        );
      }
    }
  }

  /**
   * Validates the components of a cursor-paginated request that are about
   * to be used to build a query. Centralises the schema-level guarantees
   * that `runPage`/`runSqlPage`/Supabase's `runSupabasePage` all rely on
   * before they interpolate column names into SQL or PostgREST filters.
   */
  protected validatePageRequest(request: PageRequest<Entity>): void {
    if (request.limit !== undefined) {
      if (!Number.isInteger(request.limit) || request.limit <= 0) {
        throw new StorageInvalidLimitError(request.limit);
      }
    }
    this.validateOrderBy(request.orderBy);
  }

  /**
   * Validates the `select` array in a {@link CoveringIndexQueryOptions}.
   * Throws {@link StorageValidationError} when `select` is empty or contains
   * a column that is not in the schema's `properties`.
   */
  protected validateSelect<K extends keyof Entity & string>(
    options: CoveringIndexQueryOptions<Entity, K>
  ): void {
    if (!options.select || options.select.length === 0) {
      throw new StorageValidationError("queryIndex requires a non-empty select array");
    }
    const schemaProps = Object.keys(this.schema.properties);
    for (const col of options.select) {
      const colStr = String(col);
      if (!schemaProps.includes(colStr)) {
        throw new StorageValidationError(`queryIndex select column "${colStr}" is not in schema`);
      }
    }
  }

  protected primaryKeyColumns(): Array<keyof PrimaryKey> {
    const columns: Array<keyof PrimaryKey> = [];
    for (const key of Object.keys(this.primaryKeySchema.properties)) {
      columns.push(key as keyof PrimaryKey);
    }
    return columns;
  }

  protected valueColumns(): Array<keyof Value> {
    const columns: Array<keyof Value> = [];
    for (const key of Object.keys(this.valueSchema.properties)) {
      columns.push(key as keyof Value);
    }
    return columns;
  }

  /**
   * The identity emitted on the `delete` event for a bulk `deleteSearch`: the
   * plain (operator-free) columns of the criteria — which include the owner /
   * scope columns — as a `Partial<Entity>`. Search conditions are dropped since
   * they don't identify a concrete value.
   */
  protected deleteIdentity(criteria: DeleteSearchCriteria<Entity>): Partial<Entity> {
    const identity: Record<string, unknown> = {};
    for (const [column, criterion] of Object.entries(criteria)) {
      if (!isSearchCondition(criterion)) identity[column] = criterion;
    }
    return identity as Partial<Entity>;
  }

  protected separateKeyValueFromCombined(obj: Entity): { value: Value; key: PrimaryKey } {
    if (obj === null) {
      console.warn("Key is null");
      return { value: {} as Value, key: {} as PrimaryKey };
    }
    if (typeof obj !== "object") {
      console.warn("Object is not an object");
      return { value: {} as Value, key: {} as PrimaryKey };
    }
    const primaryKeyNames = this.primaryKeyColumns();
    const valueNames = this.valueColumns();
    const value: Partial<Value> = {};
    const key: Partial<PrimaryKey> = {};
    const objRecord = obj as Record<string, unknown>;
    for (const k of primaryKeyNames) {
      (key as Record<string, unknown>)[k as string] = objRecord[k as string];
    }
    for (const k of valueNames) {
      (value as Record<string, unknown>)[k as string] = objRecord[k as string];
    }

    return { value: value as Value, key: key as PrimaryKey };
  }

  protected async getKeyAsIdString(key: PrimaryKey): Promise<string> {
    return await makeFingerprint(key);
  }

  /** Returns key values ordered to match the schema's primary-key declaration. */
  protected getPrimaryKeyAsOrderedArray(key: PrimaryKey): ValueOptionType[] {
    const orderedParams: ValueOptionType[] = [];
    const keyObj = key as Record<string, ValueOptionType>;
    for (const k in this.primaryKeySchema.properties) {
      if (k in keyObj) {
        orderedParams.push(keyObj[k]);
      } else {
        throw new Error(`Missing required primary key field: ${k}`);
      }
    }
    return orderedParams;
  }

  public findBestMatchingIndex(
    unorderedSearchKey: Array<keyof Entity>
  ): Array<keyof Entity> | undefined {
    if (!unorderedSearchKey.length) return undefined;

    const allKeys: Array<keyof Entity>[] = [
      this.primaryKeyColumns() as unknown as Array<keyof Entity>,
      ...(this.indexes as Array<keyof Entity>[]),
    ];

    const searchKeySet = new Set(unorderedSearchKey);

    const hasMatchingPrefix = (index: Array<keyof Entity>): boolean => {
      return index.length > 0 && searchKeySet.has(index[0]);
    };

    let bestMatch: Array<keyof Entity> | undefined;
    let bestMatchScore = 0;

    for (const index of allKeys) {
      if (hasMatchingPrefix(index)) {
        let score = 0;
        for (const col of index) {
          if (!searchKeySet.has(col)) break;
          score++;
        }

        if (score > bestMatchScore) {
          bestMatch = index;
          bestMatchScore = score;
        }
      }
    }

    return bestMatch;
  }

  protected hasAutoGeneratedKey(): boolean {
    return this.autoGeneratedKeyName !== null;
  }

  protected isAutoGeneratedKey(name: string): boolean {
    return this.autoGeneratedKeyName !== null && String(this.autoGeneratedKeyName) === name;
  }

  protected determineGenerationStrategy(columnName: string, typeDef: any): KeyGenerationStrategy {
    // Peel a `null` branch off of an anyOf/oneOf so a `string | null` PK column
    // still picks the "string" strategy.
    let actualType = typeDef;
    if (typeDef && typeof typeDef === "object") {
      if (typeDef.anyOf && Array.isArray(typeDef.anyOf)) {
        actualType = typeDef.anyOf.find((t: any) => t.type !== "null") || typeDef;
      } else if (typeDef.oneOf && Array.isArray(typeDef.oneOf)) {
        actualType = typeDef.oneOf.find((t: any) => t.type !== "null") || typeDef;
      }
    }

    if (typeof actualType !== "object") {
      return "uuid";
    }

    if (actualType.type === "integer") {
      return "autoincrement";
    }

    return "uuid";
  }

  /**
   * Generates a key value for client-side key generation. Override in storage
   * classes that generate keys client-side (InMemory, IndexedDB for UUIDs).
   * SQL-based storages typically generate keys server-side.
   */
  protected generateKeyValue(
    columnName: string,
    strategy: KeyGenerationStrategy
  ): Promise<string | number> | string | number {
    throw new Error(
      `generateKeyValue not implemented for ${this.constructor.name}. ` +
        `Column: ${columnName}, Strategy: ${strategy}`
    );
  }

  /**
   * Runs `fn` inside a transaction. The default implementation provides no
   * rollback semantics — it simply invokes `fn(this)`. Concrete subclasses
   * with native transaction support (SQLite, PostgreSQL) override this.
   */
  async withTransaction<T>(fn: (tx: this) => Promise<T>): Promise<T> {
    return await fn(this);
  }

  /**
   * Subclass override returning a backend-specific applier. Returning `null`
   * disables tabular migrations for the storage. The base class returns
   * `null`; only backends that have wired up their applier override this.
   */
  public getMigrationApplier(): ITabularMigrationApplier | null {
    return null;
  }

  /**
   * Runs `this.tabularMigrations` through the orchestrator using the
   * backend-supplied applier. Idempotent (no-op when no migrations are
   * declared, or when all are already applied).
   */
  protected async applyTabularMigrations(options?: RunTabularMigrationsOptions): Promise<void> {
    if (!this.tabularMigrations || this.tabularMigrations.length === 0) return;
    const applier = this.getMigrationApplier();
    if (!applier) {
      throw new Error(
        `${this.constructor.name} declared migrations but has no migration applier wired up.`
      );
    }
    await runTabularMigrations(applier, this.migrationComponent, this.tabularMigrations, options);
  }

  /**
   * Must be called before using any other methods (except for in-memory
   * implementations). Default is a no-op; override in subclasses that need setup.
   */
  async setupDatabase(): Promise<void> {}

  destroy(): void {}

  async [Symbol.asyncDispose](): Promise<void> {
    this.destroy();
  }

  [Symbol.dispose](): void {
    this.destroy();
  }
}
